"use client";

import {
  formatChartDetailDisplayValue,
  formatChartDisplayValue,
  svgChartScrubIndexForX,
  type T_SvgChartScrubGeometry,
} from "@lattice/shared-frontend/charts";
import React, { useCallback, useMemo, useRef, useState } from "react";

import type {
  T_ChartData,
  T_ChartDataPoint,
  T_DualAxisBarLineChartPresentation,
} from "@lattice/core-lib/types/chart";

import { ChartSeriesHeader } from "./ChartSeriesHeader";
import { getPointLabel, getPointValue } from "./chart-data";
import "./charts-kit.css";

const VIEWBOX_WIDTH = 300;
const VIEWBOX_HEIGHT = 190;
const X_START = 4;
const X_END = 296;
const Y_START = 10;
const Y_END = 180;

export type T_DualAxisBarLineChartProps = {
  chart: T_ChartData;
  presentation: T_DualAxisBarLineChartPresentation;
};

type T_ChartView = {
  data: T_ChartDataPoint[];
  key: string;
  label: string;
};

type T_DualAxisGeometry = {
  bars: Array<{
    fill: string;
    height: number;
    key: string;
    width: number;
    x: number;
    y: number;
  }>;
  dots: Array<{ cx: number; cy: number; key: string }>;
  leftLabels: string[];
  linePoints: string;
  rightLabels: string[];
  slots: Array<{
    barValues: [string, string];
    cx: number;
    label: string;
    lineValue: string;
  }>;
  xLabels: string[];
  zeroY: number | null;
};

/** Mirror of the mobile buildGeometry — identical scales and padding. */
function buildGeometry(args: {
  chart: T_ChartData;
  data: T_ChartDataPoint[];
  presentation: T_DualAxisBarLineChartPresentation;
}): T_DualAxisGeometry {
  const [firstBarSeries, secondBarSeries] = args.presentation.barSeries;
  const firstBarValues = args.data.map(
    (point) => getPointValue(point, firstBarSeries) ?? 0,
  );
  const secondBarValues = args.data.map(
    (point) => getPointValue(point, secondBarSeries) ?? 0,
  );
  const lineValues = args.data.map(
    (point) => getPointValue(point, args.presentation.lineSeries) ?? 0,
  );

  const leftValues = [...firstBarValues, ...secondBarValues, 0];
  let leftMinimum = Math.min(...leftValues);
  let leftMaximum = Math.max(...leftValues);
  const leftSpan = leftMaximum - leftMinimum;
  const leftPadding = (leftSpan || Math.max(Math.abs(leftMaximum), 1)) * 0.08;
  leftMaximum += leftPadding;
  if (leftMinimum < 0) leftMinimum -= leftPadding;

  const rightValues = [...lineValues, 0];
  let rightMinimum = Math.min(...rightValues);
  let rightMaximum = Math.max(...rightValues);
  const rightSpan = rightMaximum - rightMinimum;
  const rightPadding =
    (rightSpan || Math.max(Math.abs(rightMaximum), 1)) * 0.12;
  rightMaximum += rightPadding;
  if (rightMinimum < 0) rightMinimum -= rightPadding;

  const leftValueToY = (value: number) =>
    Y_END -
    ((value - leftMinimum) / (leftMaximum - leftMinimum || 1)) *
      (Y_END - Y_START);
  const rightValueToY = (value: number) =>
    Y_END -
    ((value - rightMinimum) / (rightMaximum - rightMinimum || 1)) *
      (Y_END - Y_START);
  const slotWidth = (X_END - X_START) / Math.max(args.data.length, 1);
  const barWidth = Math.min(13, slotWidth * 0.28);

  const bars = args.data.flatMap((point, index) => {
    const centerX = X_START + slotWidth * (index + 0.5);
    const label = getPointLabel(point);
    return [firstBarValues[index] ?? 0, secondBarValues[index] ?? 0].map(
      (value, seriesIndex) => {
        const positiveY = leftValueToY(Math.max(value, 0));
        const negativeY = leftValueToY(Math.min(value, 0));
        const defaultFill =
          seriesIndex === 0
            ? "var(--lattice-financial-positive)"
            : "var(--lattice-chart-net-income-bar)";
        return {
          fill: value < 0 ? "var(--lattice-financial-negative)" : defaultFill,
          height: Math.max(1, negativeY - positiveY),
          key: `${label}-${seriesIndex}`,
          width: barWidth,
          x: seriesIndex === 0 ? centerX - barWidth - 1.5 : centerX + 1.5,
          y: positiveY,
        };
      },
    );
  });
  const dots = args.data.map((point, index) => ({
    cx: X_START + slotWidth * (index + 0.5),
    cy: rightValueToY(lineValues[index] ?? 0),
    key: `${getPointLabel(point)}-${index}`,
  }));
  const formatLeft = (value: number) =>
    formatChartDisplayValue(value, {
      valueFormat: args.chart.valueFormat,
      valuePrefix: args.chart.valuePrefix,
      valueSuffix: args.chart.valueSuffix,
    });
  const formatRight = (value: number) =>
    formatChartDisplayValue(value, {
      valueFormat: args.presentation.rightValueFormat,
      valuePrefix: args.presentation.rightValuePrefix,
      valueSuffix: args.presentation.rightValueSuffix,
    });
  // Scan readout shows exact values: higher precision than axis labels.
  const formatLeftDetail = (value: number) =>
    formatChartDetailDisplayValue(value, {
      valueFormat: args.chart.valueFormat,
      valuePrefix: args.chart.valuePrefix,
      valueSuffix: args.chart.valueSuffix,
    });
  const formatRightDetail = (value: number) =>
    formatChartDetailDisplayValue(value, {
      valueFormat: args.presentation.rightValueFormat,
      valuePrefix: args.presentation.rightValuePrefix,
      valueSuffix: args.presentation.rightValueSuffix,
    });

  return {
    bars,
    dots,
    leftLabels: [leftMaximum, (leftMaximum + leftMinimum) / 2, leftMinimum].map(
      formatLeft,
    ),
    linePoints: dots
      .map((dot) => `${dot.cx.toFixed(1)},${dot.cy.toFixed(1)}`)
      .join(" "),
    rightLabels: [
      rightMaximum,
      (rightMaximum + rightMinimum) / 2,
      rightMinimum,
    ].map(formatRight),
    slots: args.data.map((point, index) => ({
      barValues: [
        formatLeftDetail(firstBarValues[index] ?? 0),
        formatLeftDetail(secondBarValues[index] ?? 0),
      ],
      cx: X_START + slotWidth * (index + 0.5),
      label: getPointLabel(point),
      lineValue: formatRightDetail(lineValues[index] ?? 0),
    })),
    xLabels: args.data.map(getPointLabel),
    zeroY: leftMinimum < 0 ? leftValueToY(0) : null,
  };
}

/**
 * Paired-bar + secondary-axis line presentation — web mirror of the mobile
 * DualAxisBarLineChart. Hand-rolled SVG with the shared scrub math; views
 * (e.g. Quarterly/Yearly) render as toggle pills in the title row.
 */
export function DualAxisBarLineChart({
  chart,
  presentation,
}: T_DualAxisBarLineChartProps) {
  const views: T_ChartView[] = presentation.views ?? [
    { data: chart.data, key: "default", label: "" },
  ];
  const initialViewKey =
    presentation.defaultViewKey ?? views[0]?.key ?? "default";
  const [selectedViewKey, setSelectedViewKey] = useState(initialViewKey);
  const selectedView =
    views.find((view) => view.key === selectedViewKey) ?? views[0];
  const geometry = useMemo(
    () =>
      buildGeometry({
        chart,
        data: selectedView?.data ?? chart.data,
        presentation,
      }),
    [chart, presentation, selectedView],
  );
  const plotRef = useRef<HTMLDivElement | null>(null);
  const [scanIndex, setScanIndex] = useState<number | null>(null);
  const scrubGeometry = useMemo<T_SvgChartScrubGeometry>(
    () => ({
      slotCount: geometry.slots.length,
      viewboxWidth: VIEWBOX_WIDTH,
      xEnd: X_END,
      xStart: X_START,
    }),
    [geometry.slots.length],
  );

  const scanFromClientX = useCallback(
    (clientX: number) => {
      const plot = plotRef.current;
      if (!plot) return;
      const rect = plot.getBoundingClientRect();
      setScanIndex(
        svgChartScrubIndexForX(clientX - rect.left, rect.width, scrubGeometry),
      );
    },
    [scrubGeometry],
  );
  const clearScan = useCallback(() => setScanIndex(null), []);

  // Header shows the scanned slot while scanning, otherwise the latest.
  const headerIndex = scanIndex ?? geometry.slots.length - 1;
  const headerSlot = geometry.slots[headerIndex];
  const [firstBarSeries, secondBarSeries] = presentation.barSeries;

  if (geometry.slots.length === 0) {
    return (
      <div className="ck-root">
        <div className="ck-empty">No chart data</div>
      </div>
    );
  }

  return (
    <div className="ck-root">
      <div className="ck-title-row">
        <h3 className="ck-title">{chart.title ?? "Chart"}</h3>
        {views.length > 1 ? (
          <div className="ck-view-group" role="tablist" aria-label="Chart view">
            {views.map((view) => (
              <button
                aria-selected={view.key === selectedViewKey}
                className={
                  view.key === selectedViewKey
                    ? "ck-view-pill ck-view-pill--active"
                    : "ck-view-pill"
                }
                key={view.key}
                onClick={() => {
                  setSelectedViewKey(view.key);
                  setScanIndex(null);
                }}
                role="tab"
                type="button"
              >
                {view.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {headerSlot ? (
        <ChartSeriesHeader
          entries={[
            {
              color: "var(--lattice-financial-positive)",
              name: firstBarSeries,
              value: headerSlot.barValues[0],
            },
            {
              color: "var(--lattice-chart-net-income-bar)",
              name: secondBarSeries,
              value: headerSlot.barValues[1],
            },
            {
              color: "var(--lattice-financial-warning)",
              name: presentation.lineSeries,
              value: headerSlot.lineValue,
            },
          ]}
          label={headerSlot.label}
        />
      ) : null}

      <div style={{ display: "flex", gap: 8 }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            justifyContent: "space-between",
            paddingBottom: 20,
          }}
        >
          {geometry.leftLabels.map((label) => (
            <span
              key={label}
              style={{
                color: "var(--lattice-chart-axis-label)",
                fontSize: "var(--lattice-font-size-xs)",
                fontWeight: 600,
                textAlign: "right",
              }}
            >
              {label}
            </span>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            className="ck-plot ck-plot--scan"
            onMouseLeave={clearScan}
            onMouseMove={(event) => scanFromClientX(event.clientX)}
            onTouchEnd={clearScan}
            onTouchMove={(event) => {
              const touch = event.touches[0];
              if (touch) scanFromClientX(touch.clientX);
            }}
            onTouchStart={(event) => {
              const touch = event.touches[0];
              if (touch) scanFromClientX(touch.clientX);
            }}
            ref={plotRef}
          >
            <svg
              className="ck-svg-plot"
              preserveAspectRatio="none"
              viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
              width="100%"
            >
              {[Y_START, (Y_START + Y_END) / 2, Y_END].map((gridY) => (
                <line
                  key={gridY}
                  stroke="var(--lattice-chart-grid)"
                  strokeWidth={1}
                  x1={0}
                  x2={VIEWBOX_WIDTH}
                  y1={gridY}
                  y2={gridY}
                />
              ))}
              {geometry.zeroY !== null ? (
                <line
                  stroke="var(--lattice-chart-zero-line)"
                  strokeWidth={1}
                  x1={0}
                  x2={VIEWBOX_WIDTH}
                  y1={geometry.zeroY}
                  y2={geometry.zeroY}
                />
              ) : null}
              {scanIndex !== null && headerSlot ? (
                <line
                  stroke="var(--lattice-chart-zero-line)"
                  strokeWidth={1.4}
                  x1={headerSlot.cx}
                  x2={headerSlot.cx}
                  y1={Y_START}
                  y2={Y_END}
                />
              ) : null}
              {geometry.bars.map((bar) => (
                <rect
                  fill={bar.fill}
                  height={bar.height}
                  key={bar.key}
                  rx={1.5}
                  width={bar.width}
                  x={bar.x}
                  y={bar.y}
                />
              ))}
              <polyline
                fill="none"
                points={geometry.linePoints}
                stroke="var(--lattice-financial-warning)"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.6}
              />
              {geometry.dots.map((dot, index) => (
                <circle
                  cx={dot.cx}
                  cy={dot.cy}
                  fill="var(--lattice-chart-dot-core)"
                  key={dot.key}
                  r={index === scanIndex ? 4.2 : 3.2}
                  stroke="var(--lattice-financial-warning)"
                  strokeWidth={2}
                />
              ))}
            </svg>
          </div>
          <div style={{ display: "flex", marginTop: 8 }}>
            {geometry.xLabels.map((label, index) => (
              <span
                key={`${label}-${index}`}
                style={{
                  color:
                    index === scanIndex
                      ? "var(--lattice-palette-white-pure)"
                      : "var(--lattice-chart-axis-label)",
                  flex: 1,
                  fontSize: "var(--lattice-font-size-xs)",
                  fontWeight: 600,
                  textAlign: "center",
                }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            flexShrink: 0,
            justifyContent: "space-between",
            paddingBottom: 20,
          }}
        >
          {geometry.rightLabels.map((label) => (
            <span
              key={label}
              style={{
                color: "var(--lattice-chart-margin-axis)",
                fontSize: "var(--lattice-font-size-xs)",
                fontWeight: 600,
                textAlign: "left",
              }}
            >
              {label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
